import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../api/axiosInstance';

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (iso, opts) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('en-IN', opts).format(new Date(iso)); }
  catch { return '—'; }
};

const fmtMonth  = (iso) => fmt(iso, { month: 'short', year: 'numeric' });
const fmtDate   = (iso) => fmt(iso, { day: '2-digit', month: 'short', year: 'numeric' });
const fmtDT     = (iso) => fmt(iso, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const inr       = (n)   => Number(n || 0).toLocaleString('en-IN');

// ─── status badge ─────────────────────────────────────────────────────────────

const STATUS_CFG = {
  paid:     { bg: 'bg-emerald-100 dark:bg-emerald-900/40', tx: 'text-emerald-700 dark:text-emerald-300', br: 'border-emerald-200 dark:border-emerald-700', dot: 'bg-emerald-500', label: 'Paid' },
  upcoming: { bg: 'bg-blue-50 dark:bg-blue-900/30',        tx: 'text-blue-700 dark:text-blue-300',       br: 'border-blue-200 dark:border-blue-700',       dot: 'bg-blue-500',   label: 'Upcoming' },
  overdue:  { bg: 'bg-red-50 dark:bg-red-900/30',          tx: 'text-red-700 dark:text-red-400',         br: 'border-red-200 dark:border-red-700',         dot: 'bg-red-500',    label: 'Overdue' },
  pending:  { bg: 'bg-amber-50 dark:bg-amber-900/30',      tx: 'text-amber-700 dark:text-amber-300',     br: 'border-amber-200 dark:border-amber-700',     dot: 'bg-amber-500',  label: 'Pending' },
  rejected: { bg: 'bg-rose-50 dark:bg-rose-900/30',        tx: 'text-rose-700 dark:text-rose-400',       br: 'border-rose-200 dark:border-rose-700',       dot: 'bg-rose-500',   label: 'Rejected' },
};

const StatusBadge = ({ status, xs }) => {
  const s = (status || '').toLowerCase();
  const c = STATUS_CFG[s] || {
    bg: 'bg-slate-100 dark:bg-slate-700', tx: 'text-slate-500 dark:text-slate-400',
    br: 'border-slate-200 dark:border-slate-600', dot: 'bg-slate-400', label: status || 'Due',
  };
  return (
    <span className={`inline-flex items-center rounded-full font-bold border uppercase tracking-wider
      ${c.bg} ${c.tx} ${c.br}
      ${xs ? 'px-1.5 py-0.5 text-[9px] gap-1' : 'px-2 py-0.5 text-[10px] gap-1.5'}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`}/>
      {c.label}
    </span>
  );
};

// ─── installment card (mobile / tablet) ───────────────────────────────────────

const InstCard = ({ inst }) => {
  const isPaid     = inst._type === 'paid';
  const hasOverdue = inst.overdue > 0;
  const hasPenalty = Number(inst.penalty) > 0;

  return (
    <div className={`rounded-2xl border p-3.5 transition-all hover:shadow-md active:scale-[.99]
      ${isPaid
        ? 'bg-emerald-50/70 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-800/40'
        : hasOverdue
        ? 'bg-red-50/60 dark:bg-red-900/10 border-red-100 dark:border-red-800/40'
        : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700'}`}>

      {/* Top row */}
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-extrabold border shadow-sm flex-shrink-0
            ${isPaid    ? 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300'
            : hasOverdue ? 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-700 text-red-600 dark:text-red-400'
            : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300'}`}>
            {inst.no}
          </div>
          <div>
            <StatusBadge status={inst.status} xs/>
            {hasOverdue && <p className="text-[9px] font-bold text-red-500 mt-0.5">{inst.overdue}d overdue</p>}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className={`text-base font-black leading-tight
            ${isPaid ? 'text-emerald-600 dark:text-emerald-400'
            : hasPenalty ? 'text-red-600 dark:text-red-400'
            : 'text-slate-800 dark:text-slate-100'}`}>
            ₹{inr(isPaid ? inst.amount : inst.total)}
          </p>
          {hasPenalty && <p className="text-[9px] font-bold text-red-400 mt-0.5">+₹{inr(inst.penalty)} penalty</p>}
        </div>
      </div>

      <div className="border-t border-slate-100 dark:border-slate-700/60 mb-2.5"/>

      {/* Bottom info */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        {isPaid ? (
          <>
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Paid On</p>
              <p className="text-[10px] sm:text-xs font-semibold text-slate-700 dark:text-slate-300 leading-tight">{fmtDT(inst.paid_at)}</p>
            </div>
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Month</p>
              <p className="text-[10px] sm:text-xs font-semibold text-slate-700 dark:text-slate-300">{fmtMonth(inst.month)}</p>
            </div>
            {inst.payment_id && (
              <div className="col-span-2">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Payment ID</p>
                <span className="text-[10px] font-bold text-blue-700 dark:text-blue-400 font-mono bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 px-2 py-0.5 rounded-lg break-all">
                  {inst.payment_id}
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Due Date</p>
              <p className="text-[10px] sm:text-xs font-semibold text-slate-700 dark:text-slate-300">{fmtDate(inst.due)}</p>
            </div>
            <div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Deadline</p>
              <p className={`text-[10px] sm:text-xs font-semibold ${hasOverdue ? 'text-red-500' : 'text-slate-700 dark:text-slate-300'}`}>
                {fmtDate(inst.deadline)}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── main ─────────────────────────────────────────────────────────────────────

const  Installments = () => {
  const [paidData,      setPaidData]      = useState([]);
  const [remainingData, setRemainingData] = useState([]);
  const [plan,          setPlan]          = useState(null);
  const [isLoading,     setIsLoading]     = useState(true);
  const [error,         setError]         = useState(null);
  const [filter,        setFilter]        = useState('all');

  const [alert, setAlert] = useState({ open: false, title: '', message: '' });
  const showAlert  = (title, message) => setAlert({ open: true, title, message });
  const closeAlert = () => setAlert(p => ({ ...p, open: false }));

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [paidRes, remRes, planRes] = await Promise.all([
        axiosInstance.get('/installments/all'),
        axiosInstance.get('/installments/remaining'),
        axiosInstance.get('/plan/get'),
      ]);

      const rawPaid = Array.isArray(paidRes.data) ? paidRes.data
        : paidRes.data?.data ?? paidRes.data?.installments ?? [];
      setPaidData(rawPaid);

      const rawRem = remRes.data?.remaining
        ?? (Array.isArray(remRes.data) ? remRes.data : []);
      setRemainingData(rawRem);

      const rawPlan = Array.isArray(planRes.data) ? planRes.data[0] : planRes.data;
      setPlan(rawPlan ?? null);
    } catch (err) {
      let msg = 'Failed to load installments.';
      if (err.response?.status === 500)     msg = 'Server error — please try again later.';
      else if (err.response?.data?.message) msg = err.response.data.message;
      else if (err.response?.data?.error)   msg = err.response.data.error;
      showAlert('Fetch Failed', msg);
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // derived
  const totalInst    = plan?.duration ?? 0;
  const totalAmt     = parseFloat(plan?.total ?? 0);
  const paidCount    = paidData.length;
  const paidAmt      = paidData.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const remCount     = remainingData.length;
  const penaltyTotal = remainingData.reduce((s, i) => s + Number(i.penalty || 0), 0);
  const outstanding  = remainingData.reduce((s, i) => s + Number(i.total || 0), 0);
  const pct          = totalInst > 0 ? Math.round((paidCount / totalInst) * 100) : 0;

  const merged = [
    ...paidData.map(i => ({ ...i, _type: 'paid' })),
    ...remainingData.map(i => ({ ...i, _type: 'remaining' })),
  ].sort((a, b) => Number(a.no) - Number(b.no));

  const displayed = filter === 'paid'      ? merged.filter(i => i._type === 'paid')
                  : filter === 'remaining' ? merged.filter(i => i._type === 'remaining')
                  : merged;

  const tabs = [
    { key: 'all',       label: 'All',       count: merged.length },
    { key: 'paid',      label: 'Paid',      count: paidCount },
    { key: 'remaining', label: 'Remaining', count: remCount },
  ];

  return (
    <div className="flex flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-100
                    dark:from-slate-950 dark:via-slate-900 dark:to-slate-950
                    min-h-screen lg:h-[calc(100vh-80px)] lg:overflow-hidden">

      <style>{`
        @keyframes blowUp { from{transform:scale(.88);opacity:0} to{transform:scale(1);opacity:1} }
        .blow { animation: blowUp .25s cubic-bezier(.175,.885,.32,1.275) forwards; }
        .sb::-webkit-scrollbar{width:4px;height:4px}
        .sb::-webkit-scrollbar-track{background:transparent}
        .sb::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:99px}
        .dark .sb::-webkit-scrollbar-thumb{background:#475569}
        .hs{-ms-overflow-style:none;scrollbar-width:none}
        .hs::-webkit-scrollbar{display:none}
      `}</style>

      {/* ══ HEADER ZONE ════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 px-3 pt-3 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6">

        {/* Breadcrumb */}
        <nav className="mb-2.5">
          <ol className="flex items-center gap-1 text-[11px] sm:text-xs text-slate-400">
            <li>
              <Link to="/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium">
                Dashboard
              </Link>
            </li>
            <li><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg></li>
            <li className="font-semibold text-slate-700 dark:text-slate-200">Installments</li>
          </ol>
        </nav>

        {/* Title + Refresh */}
        <div className="flex items-start justify-between gap-2 mb-4 sm:mb-5">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Installment Tracking
            </h1>
            {plan && (
              <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
                {plan.name}
                <span className="mx-1 text-slate-300 dark:text-slate-600">·</span>
                {totalInst} months
                <span className="mx-1 text-slate-300 dark:text-slate-600">·</span>
                ₹{inr(totalAmt)} total
              </p>
            )}
          </div>
          <button
            onClick={fetchAll} disabled={isLoading}
            className="flex-shrink-0 flex items-center gap-1.5 px-2.5 sm:px-3.5 py-2 rounded-xl bg-white dark:bg-slate-800
                       border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400
                       hover:border-blue-400 hover:text-blue-600 transition-all shadow-sm text-xs font-semibold
                       disabled:opacity-50 active:scale-95">
            <svg className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin text-blue-500' : ''}`}
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* ── STAT CARDS ─────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-5">

          {/* Progress — spans full width on mobile for prominence */}
          <div className="col-span-2 lg:col-span-1 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 px-4 py-3.5 sm:p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Progress</p>
              <span className="text-xs font-black text-blue-600">{pct}%</span>
            </div>
            {/* Mobile: ring + text side-by-side; lg: stacked + bar */}
            <div className="flex lg:flex-col items-center lg:items-stretch gap-4 lg:gap-0">
              {/* SVG ring — only on mobile / tablet */}
              <div className="lg:hidden flex-shrink-0">
                <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="22" fill="none" stroke="#e2e8f0" strokeWidth="5"/>
                  <circle cx="28" cy="28" r="22" fill="none" stroke="#2563eb" strokeWidth="5"
                    strokeDasharray={String(2 * Math.PI * 22)}
                    strokeDashoffset={String(2 * Math.PI * 22 * (1 - pct / 100))}
                    strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }}/>
                </svg>
              </div>
              <div className="flex-1 lg:flex-none">
                <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
                  {paidCount}<span className="text-sm font-medium text-slate-400">/{totalInst}</span>
                </p>
                <p className="text-[10px] sm:text-xs text-slate-400 font-medium mt-0.5">installments paid</p>
                {/* Bar — lg+ only */}
                <div className="hidden lg:block mt-3">
                  <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                         style={{ width: `${pct}%`, transition: 'width 1s ease' }}/>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Paid amount */}
          <div className="col-span-1 relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 p-3.5 sm:p-5 shadow-lg">
            <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none"/>
            <p className="text-[9px] sm:text-[10px] font-bold text-emerald-100 uppercase tracking-widest mb-1.5 sm:mb-2">Paid</p>
            <p className="text-lg sm:text-2xl font-black text-white leading-tight">₹{inr(paidAmt)}</p>
            <p className="text-[9px] sm:text-xs text-emerald-100 font-semibold mt-1 truncate">of ₹{inr(totalAmt)}</p>
          </div>

          {/* Remaining */}
          <div className="col-span-1 relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 p-3.5 sm:p-5 shadow-lg">
            <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none"/>
            <p className="text-[9px] sm:text-[10px] font-bold text-amber-100 uppercase tracking-widest mb-1.5 sm:mb-2">Remaining</p>
            <p className="text-lg sm:text-2xl font-black text-white">{remCount}</p>
            <p className="text-[9px] sm:text-xs text-amber-100 font-semibold mt-1 truncate">₹{inr(outstanding)} due</p>
          </div>

          {/* Penalty */}
          <div className={`col-span-2 lg:col-span-1 relative overflow-hidden rounded-2xl p-3.5 sm:p-5 shadow-lg
            bg-gradient-to-br ${penaltyTotal > 0 ? 'from-rose-500 to-rose-700' : 'from-slate-500 to-slate-700'}`}>
            <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none"/>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[9px] sm:text-[10px] font-bold text-white/70 uppercase tracking-widest mb-1.5 sm:mb-2">Penalty</p>
                <p className="text-lg sm:text-2xl font-black text-white">₹{inr(penaltyTotal)}</p>
                <p className="text-[9px] sm:text-xs text-white/70 font-semibold mt-1">
                  {penaltyTotal > 0 ? 'Overdue charges' : 'No penalties 🎉'}
                </p>
              </div>
              {penaltyTotal > 0 && (
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-xl flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── FILTER TABS ──────────────────────────────────────────────────── */}
        <div className="flex items-center border-b border-slate-200 dark:border-slate-700 overflow-x-auto hs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-bold border-b-2
                         whitespace-nowrap transition-all -mb-px flex-shrink-0
                         ${filter === tab.key
                           ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                           : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-md text-[9px] sm:text-[10px] font-black
                ${filter === tab.key
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ══ CONTENT AREA ═══════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto sb px-3 pb-6 sm:px-5 lg:px-6 pt-3 sm:pt-4">

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <svg className="w-9 h-9 text-blue-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <p className="text-sm font-semibold text-slate-400">Loading your schedule…</p>
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-14 h-14 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <p className="text-sm font-semibold text-red-500 text-center px-4">{error}</p>
            <button onClick={fetchAll} className="text-sm font-bold text-blue-600 underline">Try Again</button>
          </div>
        )}

        {/* Data */}
        {!isLoading && !error && (
          <>
            {/* ── MOBILE + TABLET: Cards ── (hidden on lg+) */}
            <div className="lg:hidden">
              {displayed.length === 0 ? (
                <p className="text-center text-sm text-slate-400 font-medium py-16">No records found.</p>
              ) : (
                /* 1 col on mobile, 2 col on sm/md tablet */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  {displayed.map(inst => <InstCard key={`c-${inst.no}`} inst={inst}/>)}
                </div>
              )}
            </div>

            {/* ── DESKTOP: Table ── (hidden below lg) */}
            <div className="hidden lg:block">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="overflow-x-auto sb">
                  <table className="w-full text-left border-collapse whitespace-nowrap">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-700">
                        {['No.','Status','Amount','Penalty','Total','Due / Paid On','Deadline / Payment ID'].map(h => (
                          <th key={h} className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest first:w-16">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
                      {displayed.length === 0 ? (
                        <tr><td colSpan="7" className="py-16 text-center text-sm text-slate-400">No records found.</td></tr>
                      ) : displayed.map(inst => {
                        const isPaid     = inst._type === 'paid';
                        const hasOverdue = inst.overdue > 0;
                        const hasPenalty = Number(inst.penalty) > 0;
                        return (
                          <tr key={`r-${inst.no}`} className={`transition-colors
                            ${isPaid      ? 'hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10'
                            : hasOverdue  ? 'bg-red-50/30 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'}`}>
                            <td className="px-5 py-3.5">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-extrabold border
                                ${isPaid      ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300'
                                : hasOverdue  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-600 dark:text-red-400'
                                : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300'}`}>
                                {inst.no}
                              </div>
                            </td>
                            <td className="px-5 py-3.5"><StatusBadge status={inst.status}/></td>
                            <td className="px-5 py-3.5 text-sm font-bold text-slate-700 dark:text-slate-200">
                              ₹{inr(inst.amount)}
                            </td>
                            <td className="px-5 py-3.5">
                              {isPaid ? (
                                <span className="text-slate-300 dark:text-slate-600 text-xs">—</span>
                              ) : hasPenalty ? (
                                <span className="text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-2 py-0.5 rounded-md">
                                  +₹{inr(inst.penalty)}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">₹0</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              <span className={`text-sm font-black
                                ${isPaid ? 'text-emerald-600 dark:text-emerald-400'
                                : hasPenalty ? 'text-red-600 dark:text-red-400'
                                : 'text-slate-700 dark:text-slate-200'}`}>
                                ₹{inr(isPaid ? inst.amount : inst.total)}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              {isPaid ? (
                                <div>
                                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">{fmtDT(inst.paid_at)}</p>
                                  <p className="text-[10px] text-slate-400 mt-0.5">{fmtMonth(inst.month)}</p>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">{fmtDate(inst.due)}</p>
                                  {hasOverdue && <p className="text-[10px] font-bold text-red-500 mt-0.5">{inst.overdue}d overdue</p>}
                                </div>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              {isPaid ? (
                                inst.payment_id ? (
                                  <span className="text-[11px] font-bold text-blue-700 dark:text-blue-400 font-mono bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 px-2.5 py-1 rounded-lg">
                                    {inst.payment_id}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">N/A</span>
                                )
                              ) : (
                                <p className={`text-xs font-semibold ${hasOverdue ? 'text-red-500' : 'text-slate-600 dark:text-slate-300'}`}>
                                  {fmtDate(inst.deadline)}
                                </p>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══ ERROR MODAL ════════════════════════════════════════════════════════ */}
      {alert.open && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 w-full max-w-xs sm:max-w-sm rounded-2xl shadow-2xl overflow-hidden text-center blow">
            <div className="p-5 sm:p-6 pt-7 sm:pt-8 flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4 ring-8 ring-red-50 dark:ring-red-900/10">
                <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-black text-slate-900 dark:text-white mb-2">{alert.title}</h3>
              <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed px-1">{alert.message}</p>
            </div>
            <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-700/50 border-t border-slate-100 dark:border-slate-700">
              <button
                onClick={closeAlert}
                className="w-full py-2.5 rounded-xl text-sm font-black text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/20 transition-all active:scale-95">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Installments;